import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar'
import { Loader2, Check, X, ShieldCheck, UserPlus } from 'lucide-react'
import { toast } from '@/components/ui/sonner'
import athleteApi from '@/lib/api'
import { RootState } from '@/store'

interface Invitation {
  id: number
  status: 'pending' | 'accepted' | 'expired'
  message?: string
  inviter: {
    name: string
    avatar?: string
  }
}

const AcceptManagerInvite = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()

  // Check auth state
  const currentUser = useSelector((state: RootState) => state.user.currentUser)
  const userLoading = useSelector((state: RootState) => state.user.loading)

  const [invitation, setInvitation] = useState<Invitation | null>(null)
  const [loading, setLoading] = useState(true)
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getErrorMessage = (err: any) => {
    const errorData = err.response?.data
    if (typeof errorData?.error === 'string') return errorData.error
    if (typeof errorData?.message === 'string') return errorData.message
    if (typeof errorData === 'string') return errorData
    return 'An unexpected error occurred'
  }

  useEffect(() => {
    const fetchInvitation = async () => {
      try {
        const response = await athleteApi.get(`/athlete/invitations/${token}`)
        setInvitation(response.data)
      } catch (err: any) {
        console.error('Failed to fetch invitation:', err)
        setError(getErrorMessage(err))
      } finally {
        setLoading(false)
      }
    }

    if (token) {
      fetchInvitation()
    }
  }, [token])

  const handleAccept = async () => {
    if (!token) return

    setProcessing(true)
    try {
      await athleteApi.post(`/athlete/invitations/${token}/accept`)
      toast.success('Invitation accepted! You are now connected.')
      // Navigate to dashboard after short delay
      setTimeout(() => {
        navigate('/')
      }, 1500)
    } catch (err: any) {
      console.error('Failed to accept invitation:', err)
      toast.error(getErrorMessage(err))
      setProcessing(false)
    }
  }

  const handleDecline = () => {
    // For now just navigate away
    navigate('/')
  }

  const handleSignUp = () => {
    // Navigate to onboarding with the invitation token as a query param
    navigate(`/onboarding?invite=${token}`)
  }

  const handleSignIn = () => {
    // Navigate to sign in with return URL
    navigate(`/signin?returnTo=/manager-invite/${token}`)
  }

  // Show loading while checking auth or fetching invitation
  if (loading || userLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black">
        <Loader2 className="h-8 w-8 animate-spin text-[#BCF565]" />
      </div>
    )
  }

  if (error || !invitation) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-4">
        <Card className="w-full max-w-md bg-[#1a1a1a] border-white/10 text-white">
          <CardHeader>
            <CardTitle className="text-red-500">Invitation Error</CardTitle>
            <CardDescription className="text-gray-400">
              {error || 'Invitation not found or invalid.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-white/10 hover:bg-white/20 text-white"
              onClick={() => navigate('/signin')}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (invitation.status !== 'pending') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-black p-4">
        <Card className="w-full max-w-md bg-[#1a1a1a] border-white/10 text-white">
          <CardHeader>
            <CardTitle>
              Invitation {invitation.status === 'accepted' ? 'Accepted' : 'Expired'}
            </CardTitle>
            <CardDescription className="text-gray-400">
              This invitation is no longer valid or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055]"
              onClick={() => navigate(currentUser ? '/' : '/signin')}
            >
              {currentUser ? 'Go to Dashboard' : 'Go to Sign In'}
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  // Check if user is authenticated
  const isAuthenticated = !!currentUser

  return (
    <div className="flex min-h-screen items-center justify-center bg-black p-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#BCF565]/5 rounded-full blur-3xl pointer-events-none" />

      <Card className="w-full max-w-lg bg-[#1a1a1a]/90 backdrop-blur-xs border-white/10 text-white relative z-10">
        <CardHeader className="text-center pb-8 border-b border-white/5">
          <div className="mx-auto bg-[#BCF565]/10 p-3 rounded-full w-fit mb-4">
            <ShieldCheck className="h-8 w-8 text-[#BCF565]" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {isAuthenticated ? 'Connect with Manager' : "You've Been Invited!"}
          </CardTitle>
          <CardDescription className="text-gray-400 max-w-xs mx-auto mt-2">
            {isAuthenticated
              ? "You've been invited to join a manager's roster on Athios."
              : `${invitation.inviter.name} has invited you to join their roster on Athios.`}
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-8 space-y-8">
          {/* Manager Info */}
          <div className="flex flex-col items-center justify-center text-center space-y-3">
            <Avatar className="h-20 w-20 border-2 border-[#BCF565]/20">
              <AvatarImage src={invitation.inviter.avatar} />
              <AvatarFallback className="bg-[#BCF565]/10 text-[#BCF565] text-xl">
                {invitation.inviter.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-semibold">{invitation.inviter.name}</h3>
              <p className="text-sm text-gray-400">Manager</p>
            </div>
          </div>

          {/* Personal Message */}
          {invitation.message && (
            <div className="bg-white/5 p-4 rounded-lg border border-white/5 relative">
              <div className="absolute -top-2 left-6 bg-[#1a1a1a] px-2 text-xs text-gray-400">
                Message from {invitation.inviter.name.split(' ')[0]}
              </div>
              <p className="text-gray-300 italic text-sm">"{invitation.message}"</p>
            </div>
          )}

          {/* Info Text */}
          <div className="text-center text-sm text-gray-500 px-4">
            <p>
              {isAuthenticated
                ? 'Accepting this invitation will add you to their roster and allow them to propose deals and manage collaborations on your behalf.'
                : 'Create an account to join their roster. Once you sign up, you can connect and start receiving deal opportunities.'}
            </p>
          </div>

          {/* Actions - Different based on auth state */}
          {isAuthenticated ? (
            // Logged in: Show Accept/Decline
            <div className="grid grid-cols-2 gap-3 pt-2">
              <Button
                variant="outline"
                className="border-white/10 hover:bg-white/5 text-white h-12"
                onClick={handleDecline}
                disabled={processing}
              >
                <X className="mr-2 h-4 w-4" />
                Not Now
              </Button>
              <Button
                className="bg-[#BCF565] text-black hover:bg-[#a8e055] h-12"
                onClick={handleAccept}
                disabled={processing}
              >
                {processing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Check className="mr-2 h-4 w-4" />
                )}
                Accept & Connect
              </Button>
            </div>
          ) : (
            // Not logged in: Show Sign Up / Sign In
            <div className="space-y-3 pt-2">
              <Button
                className="w-full bg-[#BCF565] text-black hover:bg-[#a8e055] h-12"
                onClick={handleSignUp}
              >
                <UserPlus className="mr-2 h-4 w-4" />
                Create Account
              </Button>
              <Button
                variant="outline"
                className="w-full border-white/10 hover:bg-white/5 text-white h-12"
                onClick={handleSignIn}
              >
                Already have an account? Sign In
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export default AcceptManagerInvite
